import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { checkInstructions } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useAssignmentChanges } from '../../hooks/assignment-changes';
import { useField, validateAll } from '../../hooks/field';
import { useInstruments } from '../../hooks/instruments';
import { usePatientRecord } from '../../hooks/patient-record';
import { useVisit } from '../../hooks/visit';

import { BackLink } from '../back-link';
import { Button } from '../button';
import { InstrumentPicker } from '../instrument-picker';
import { LoadFailed } from '../load-failed';
import { NextSteps } from '../next-steps';
import { TextArea } from '../text-area';

import styles from './style.module.css';

// Assign a test (C34), as drawn in the blueprint: choose a questionnaire,
// read which language the patient will get it in, add instructions if
// wanted, and assign. Under the form, what already waits for the patient.
// There is no due date, linked session or repeat. Assigning returns to the
// patient's Tests tab, where the new test is listed.
export function AssignTest() {
  const { text, translate } = useLanguage();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const capture = useVisit();
  const record = usePatientRecord(patientId);
  const { instruments, error: instrumentsError, retry: retryInstruments } = useInstruments();
  const { assign } = useAssignmentChanges();
  const instructions = useField({ initial: '', check: checkInstructions });
  const [ chosen, setChosen ] = useState();
  const [ sending, setSending ] = useState(false);
  const [ error, setError ] = useState();

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (chosen == null) {
      setError(text.chooseQuestionnaire);

      return null;
    }

    if (validateAll([ instructions ]) === false) {
      return null;
    }

    setSending(true);
    setError();

    // The assignment is made even if the clinician leaves meanwhile; only
    // the move to Tests and the error belong to this visit (#135).
    const owns = capture();

    return assign({ patientId, code: chosen.code, version: chosen.version, instructions: instructions.value }).then(function() {
      if (owns() === true) {
        navigate(`/patients/${ patientId }/tests`);
      }
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setSending(false);
      setError(failed.message);
    });
  }, [ chosen, instructions, assign, patientId, navigate, text, capture ]);

  const choose = useCallback(function(instrument) {
    setChosen(instrument);
    setError();
  }, []);

  if (record.error != null) {
    return <LoadFailed onRetry={ record.retry }/>;
  }

  if (instrumentsError != null) {
    return <LoadFailed onRetry={ retryInstruments }/>;
  }

  if (record.data === undefined || instruments === undefined) {
    return null;
  }

  const { patient, patientAssignments } = record.data;
  const name = patient.firstName;
  const patientLanguage = text.languageNames[ patient.language ];

  // Which language the patient will get, by the rule the service applies:
  // theirs when the questionnaire has it, else English, else the only one.
  let hint = text.shownInLanguage.replace('{name}', name).replace('{language}', patientLanguage);

  if (chosen != null && chosen.languages.includes(patient.language) === false && chosen.languages.includes('en') === true) {
    hint = text.shownInEnglish.replace('{name}', name).replace(/\{language\}/g, patientLanguage);
  }

  if (chosen != null && chosen.languages.includes(patient.language) === false && chosen.languages.includes('en') === false) {
    hint = text.shownOnlyIn.replace(/\{language\}/g, text.languageNames[ chosen.languages[ 0 ] ]);
  }

  let message = null;

  if (error != null) {
    message = <span className={ styles.error } role='alert'>{ translate(error) }</span>;
  }

  const waiting = patientAssignments.filter(function({ open }) {
    return open === true;
  });

  let waitingFor = null;

  if (waiting.length > 0) {
    waitingFor = (
      <section className={ styles.waiting } aria-labelledby='waiting-for'>
        <h2 id='waiting-for' className={ styles.heading }>{ text.waitingFor.replace('{name}', name) }</h2>

        <ul className={ styles.list }>
          {
            waiting.map(function({ id, questionnaire }) {
              return <li key={ id } className={ styles.item }>{ questionnaire.name }</li>;
            })
          }
        </ul>
      </section>
    );
  }

  return (
    <section className={ styles.page }>
      <BackLink to={ `/patients/${ patientId }/tests` }>{ `${ patient.firstName } ${ patient.lastName }` }</BackLink>

      <h1 className={ styles.title }>{ text.assignATest }</h1>

      <form className={ styles.form } onSubmit={ onSubmit } noValidate>
        <InstrumentPicker instruments={ instruments } chosen={ chosen } onChoose={ choose }/>

        <p className={ styles.hint }>{ hint }</p>

        <TextArea
          ref={ instructions.ref }
          id='assign-instructions'
          label={ text.instructionsFor.replace('{name}', name) }
          value={ instructions.value }
          onChange={ instructions.onChange }
          onBlur={ instructions.onBlur }
          error={ translate(instructions.error) }
        />

        <NextSteps
          title={ text.whatHappensNext }
          steps={ [ text.assignStep1.replace('{name}', name), text.assignStep2.replace('{name}', name), text.assignStep3.replace('{name}', name) ] }
        />

        <Button type='submit' disabled={ sending }>{ text.assignTo.replace('{name}', name) }</Button>

        { message }
      </form>

      { waitingFor }
    </section>
  );
}
